import {
    Flex,
    List,
    Button,
    useStyles,
    Caption,
    useTheme,
    pxToRem,
} from '@archipro-design/aria';
import { StarFill } from '@archipro-design/icons';
import { Link, useLoaderData } from '@remix-run/react';
import { localizeTime } from '@modules/root';
import * as S from './Stats.style';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import { useSaveToDesignBoard } from '@modules/design-board/hook';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import Associations from '@modules/project/component/about-professional/Associations';
import makeSocialLinks from '@modules/project/util/make-social-links';
import SocialLinks from '@modules/project/component/about-professional/SocialLinks';
import Locations from '@modules/project/component/about-professional/Locations';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import type { ProjectPageLoader } from '@modules/project/type';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

interface CompanyInfoStatsProps {
    data: ProjectPageLoader['project']['Professional'];
}

const getMetricData = (data: ProjectPageLoader['project']['Professional']) => {
    const memberSince = data.ProfileLiveDate
        ? localizeTime(data.ProfileLiveDate).format('YYYY')
        : null;
    const visibleLocations = data.LibraryLocations.filter(
        (i) => !i.LocationHidden
    );

    return {
        projectCount: String(data.ProjectCount),
        yearFounded: String(data.YearFounded),
        enquiryResponseRate: data.EnquiryResponseRate,
        webUrl: data.Website,
        profilePage: `/professional/${data.URLSegment}`,
        memberSince,
        visibleLocations,
        associations: data.Associations.edges ?? [],
        socialLinks: makeSocialLinks(data),
    };
};

const CompanyInfoStats = ({ data }: CompanyInfoStatsProps) => {
    const { css } = useStyles();
    const { siteVariables } = useTheme();
    const { mobile } = useAppDisplayModeContext();

    const { project } = useLoaderData<typeof loader>();

    const REVIEWS_READY = false;

    const {
        projectCount,
        yearFounded,
        webUrl,
        profilePage,
        memberSince,
        visibleLocations,
        associations,
        socialLinks,
    } = getMetricData(data);

    const { favouriteItem } = useSaveToDesignBoard({
        itemType: 'ProfessionalPin',
        itemId: data.ID,
    });

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(webUrl, {
            libraryItemID: project.ID,
            professionalID: project.Professional.ID,
            professionalTitle: project.Professional.Title,
        });

    const companyInfoStats = [
        yearFounded
            ? {
                  label: 'Year founded',
                  customChildComponent: (
                      <Flex data-testid="year-founded">{yearFounded}</Flex>
                  ),
              }
            : null,
        memberSince
            ? {
                  label: 'ArchiPro Member since',
                  customChildComponent: (
                      <Flex data-testid="member-since">{memberSince}</Flex>
                  ),
              }
            : null,
        associations.length
            ? {
                  label: 'Associations',
                  customChildComponent: <Associations data={associations} />,
              }
            : null,
    ];

    const follow = socialLinks.length
        ? {
              label: 'Follow',
              customChildComponent: <SocialLinks links={socialLinks} />,
          }
        : null;

    // ECOM-1087/1088: OUT OF SCOPE.
    const reviews = REVIEWS_READY && {
        label: 'Reviews',
        customChildComponent: (
            <Flex>
                <Flex className={css(S.ReviewsStarContainer)}>
                    {[...Array(5)].map((_, i) => (
                        <StarFill
                            key={`review-star-${i}`}
                            className={css(S.StarIcon)}
                        />
                    ))}
                </Flex>

                <Caption className={css(S.ReviewsCount)}>(6 reviews)</Caption>
            </Flex>
        ),
    };

    const locations = visibleLocations.length > 0 && {
        label: 'Locations',
        customChildComponent: (
            <Locations
                profilePage={profilePage}
                locations={visibleLocations}
                professionalID={data.ID}
            />
        ),
    };

    if (mobile) {
        if (projectCount) {
            companyInfoStats.unshift({
                label: 'Projects published',
                customChildComponent: (
                    <Flex data-testid="projects-count">{projectCount}</Flex>
                ),
            });
        }
        if (locations) companyInfoStats.push(locations);
        companyInfoStats.push(follow);
    } else {
        companyInfoStats.push(follow);

        if (locations) {
            companyInfoStats.push(locations);
        }
        if (reviews) {
            companyInfoStats.push(reviews);
        }

        companyInfoStats.push({
            label: 'More information',
            customChildComponent: (
                <Flex space="between" className={css(S.WebsiteStat)}>
                    {referralTrackingURL ? (
                        <Button
                            as="a"
                            href={referralTrackingURL}
                            target="_blank"
                            size={16}
                            text
                            color="dark"
                            variables={{
                                darkButtonTextColorHover:
                                    siteVariables.colors.grey['200'],
                                buttonPadding: 0,
                            }}
                            className={css({
                                textDecoration: 'underline!important',
                            })}
                            data-testid="website-link"
                            {...referralTrackingParams}
                        >
                            Visit Professional's website
                        </Button>
                    ) : (
                        <div />
                    )}
                    <Flex
                        vAlign="center"
                        gap="space-24"
                        className={css(S.FavouriteGroup)}
                    >
                        <Button
                            color="dark"
                            className={css(S.ProfileButton)}
                            as={Link}
                            to={profilePage}
                            size={16}
                            variables={{
                                buttonSize24LetterSpacing:
                                    siteVariables.letterSpacingMedium,
                            }}
                        >
                            View Profile
                        </Button>
                        <SaveToDesignBoard
                            {...favouriteItem}
                            heartButton={{
                                className: css(S.HeartButton),
                                variables: {
                                    buttonSize36IconXMargin: '0',
                                    buttonPadding: pxToRem(8),
                                },
                            }}
                        />
                    </Flex>
                </Flex>
            ),
        });
    }

    return (
        <div className={css(S.CompanyInfoContainer)}>
            <List
                items={companyInfoStats}
                className={css(S.CompanyInfoList)}
                variables={{
                    listBorderColor: siteVariables.colors.primitive.black,
                }}
            />
        </div>
    );
};

export default CompanyInfoStats;
